<div class="p-6 space-y-5">
  <div class="flex items-center justify-between">
    <div>
      <h1 class="text-2xl font-bold text-gray-900 dark:text-white">
        {{ 'ADMIN.PLANS.TITLE' | translate }}
      </h1>
      <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
        {{ 'ADMIN.PLANS.SUBTITLE' | translate }}
      </p>
    </div>
    <div class="flex gap-2">
      <button
        (click)="load()"
        class="p-2 rounded-lg border border-gray-300 dark:border-gray-700 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
      >
        <lucide-icon [img]="RefreshCw" class="w-4 h-4"></lucide-icon>
      </button>
      <button
        (click)="showArchived.set(!showArchived())"
        [class.bg-amber-100]="showArchived()"
        [class.dark:bg-amber-900/30]="showArchived()"
        [class.text-amber-700]="showArchived()"
        [class.dark:text-amber-400]="showArchived()"
        [class.border-amber-300]="showArchived()"
        [class.dark:border-amber-700]="showArchived()"
        class="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-sm"
      >
        <lucide-icon [img]="Archive" class="w-4 h-4"></lucide-icon>
        {{ 'ADMIN.PLANS.ARCHIVE_SHOW' | translate }}
      </button>
      <button
        (click)="openCreatePlan()"
        class="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
      >
        <lucide-icon [img]="Plus" class="w-4 h-4"></lucide-icon>
        {{ 'ADMIN.PLANS.ADD' | translate }}
      </button>
    </div>
  </div>

  @if (loading()) {
    <div class="grid grid-cols-1 md:grid-cols-3 gap-5">
      @for (i of [1, 2, 3]; track i) {
        <div class="bg-white dark:bg-gray-800 rounded-xl p-6 animate-pulse h-64"></div>
      }
    </div>
  } @else {
    <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
      @for (plan of filteredPlans(); track plan.id) {
        <div
          class="bg-white dark:bg-gray-800 rounded-xl border flex flex-col overflow-hidden transition-opacity"
          [class.border-amber-300]="plan.isArchived"
          [class.dark:border-amber-700]="plan.isArchived"
          [class.opacity-70]="plan.isArchived"
          [class.border-gray-200]="!plan.isArchived"
          [class.dark:border-gray-700]="!plan.isArchived"
        >
          <!-- Plan header -->
          <div class="p-5 border-b border-gray-100 dark:border-gray-700">
            <div class="flex items-start justify-between gap-2">
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 flex-wrap">
                  <h3 class="font-bold text-gray-900 dark:text-white text-lg">
                    {{ localized(plan, 'name') || plan.name }}
                  </h3>
                  @if (plan.isArchived) {
                    <span
                      class="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 rounded-full"
                      >{{ 'ADMIN.PLANS.ARCHIVE_BADGE' | translate }}</span
                    >
                  } @else if (plan.isActive) {
                    <span
                      class="text-xs px-2 py-0.5 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-full"
                      >{{ 'ADMIN.PLANS.ACTIVE' | translate }}</span
                    >
                  } @else {
                    <span
                      class="text-xs px-2 py-0.5 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 rounded-full"
                      >{{ 'ADMIN.PLANS.INACTIVE' | translate }}</span
                    >
                  }
                  <span
                    class="text-xs px-2 py-0.5 rounded-full"
                    [class.bg-blue-100]="plan.isView"
                    [class.text-blue-700]="plan.isView"
                    [class.dark:bg-blue-900/30]="plan.isView"
                    [class.dark:text-blue-400]="plan.isView"
                    [class.bg-gray-100]="!plan.isView"
                    [class.text-gray-600]="!plan.isView"
                    [class.dark:bg-gray-700/40]="!plan.isView"
                    [class.dark:text-gray-300]="!plan.isView"
                  >
                    {{ plan.isView ? 'Ko‘rinadi' : 'Yashirin' }}
                  </span>
                </div>
                @if (localized(plan, 'description')) {
                  <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {{ localized(plan, 'description') }}
                  </p>
                }
              </div>
              <div class="flex gap-1 shrink-0">
                <button
                  (click)="openEditPlan(plan)"
                  class="p-1.5 rounded-md text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                >
                  <lucide-icon [img]="Edit" class="w-4 h-4"></lucide-icon>
                </button>
                <button
                  (click)="togglePlan(plan)"
                  class="p-1.5 rounded-md text-gray-400 hover:text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 transition-colors"
                >
                  <lucide-icon
                    [img]="plan.isActive ? ToggleRight : ToggleLeft"
                    class="w-4 h-4"
                  ></lucide-icon>
                </button>
                <button
                  (click)="archivePlan(plan)"
                  [title]="
                    (plan.isArchived
                      ? 'ADMIN.PLANS.UNARCHIVE_TOOLTIP'
                      : 'ADMIN.PLANS.ARCHIVE_TOOLTIP'
                    ) | translate
                  "
                  class="p-1.5 rounded-md text-gray-400 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors"
                >
                  <lucide-icon
                    [img]="plan.isArchived ? ArchiveRestore : Archive"
                    class="w-4 h-4"
                  ></lucide-icon>
                </button>
                <button
                  (click)="deletePlan(plan)"
                  class="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                >
                  <lucide-icon [img]="Trash2" class="w-4 h-4"></lucide-icon>
                </button>
              </div>
            </div>

            <div class="mt-3">
              <span class="text-2xl font-bold text-gray-900 dark:text-white">{{
                plan.monthlyPrice | number: '1.0-0'
              }}</span>
              <span class="text-sm text-gray-500 ml-1">UZS/oy</span>
            </div>

            <div class="mt-3 grid grid-cols-2 gap-2 text-xs text-gray-600 dark:text-gray-400">
              <div class="flex items-center gap-1">
                <div class="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                {{ 'ADMIN.PLANS.STORES_LIMIT' | translate }}
                <strong class="text-gray-900 dark:text-white ml-1">{{ plan.maxStores }}</strong>
              </div>
              <div class="flex items-center gap-1">
                <div class="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                {{ 'ADMIN.PLANS.EMPLOYEES_LIMIT' | translate }}
                <strong class="text-gray-900 dark:text-white ml-1">{{ plan.maxEmployees }}</strong>
              </div>
              <div class="flex items-center gap-1 col-span-2">
                <div class="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                {{ 'ADMIN.PLANS.PRODUCTS_LIMIT' | translate }}
                <strong class="text-gray-900 dark:text-white ml-1">
                  {{
                    plan.maxProducts > 0 ? plan.maxProducts : ('ADMIN.PLANS.UNLIMITED' | translate)
                  }}
                </strong>
              </div>
            </div>
            <div class="mt-3 flex flex-wrap gap-1.5">
              @if (hasModule(plan, 'attendance')) {
                <span
                  class="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                >
                  Attendance
                </span>
              }
              @if (hasModule(plan, 'product_images')) {
                <span
                  class="inline-flex items-center rounded-full bg-purple-50 px-2 py-0.5 text-xs font-medium text-purple-700 dark:bg-purple-900/30 dark:text-purple-300"
                >
                  {{ 'ADMIN.PLANS.MODULE_PRODUCT_IMAGES' | translate }}
                </span>
              }
            </div>
          </div>

          <!-- Discounts section -->
          <div class="p-5 flex-1">
            <div class="flex items-center justify-between mb-3">
              <span
                class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide"
                >{{ 'ADMIN.PLANS.DISCOUNTS' | translate }}</span
              >
              <button
                (click)="openAddDiscount(plan.id)"
                class="flex items-center gap-1 text-xs text-green-600 hover:text-green-700 dark:text-green-400 font-medium"
              >
                <lucide-icon [img]="Plus" class="w-3 h-3"></lucide-icon>
                {{ 'ADMIN.PLANS.ADD_DISCOUNT' | translate }}
              </button>
            </div>

            @if (plan.discounts.length === 0) {
              <p class="text-xs text-gray-400 italic">
                {{ 'ADMIN.PLANS.NO_DISCOUNTS' | translate }}
              </p>
            } @else {
              <div class="space-y-2">
                @for (d of plan.discounts; track d.id) {
                  <div
                    class="flex items-center justify-between p-2 rounded-lg"
                    [class.bg-green-50]="d.isActive"
                    [class.dark:bg-green-900/10]="d.isActive"
                    [class.bg-gray-50]="!d.isActive"
                    [class.dark:bg-gray-700/30]="!d.isActive"
                  >
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-1.5">
                        <lucide-icon
                          [img]="Percent"
                          class="w-3 h-3 text-green-600 dark:text-green-400 shrink-0"
                        ></lucide-icon>
                        <span class="text-xs font-semibold text-green-700 dark:text-green-400"
                          >{{ d.discountPercent }}%</span
                        >
                        <span class="text-xs text-gray-500 dark:text-gray-400"
                          >— {{ discountTypeLabel(d.minMonths) }}</span
                        >
                        @if (!d.isActive) {
                          <span class="text-xs text-red-500">{{
                            'ADMIN.PLANS.INACTIVE_BADGE' | translate
                          }}</span>
                        }
                      </div>
                      <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                        {{ d.description }}
                      </p>
                    </div>
                    <div class="flex gap-1 ml-2 shrink-0">
                      <button
                        (click)="openEditDiscount(plan.id, d)"
                        class="p-1 rounded text-gray-400 hover:text-green-600 transition-colors"
                      >
                        <lucide-icon [img]="Edit" class="w-3.5 h-3.5"></lucide-icon>
                      </button>
                      <button
                        (click)="deleteDiscount(d.id)"
                        class="p-1 rounded text-gray-400 hover:text-red-600 transition-colors"
                      >
                        <lucide-icon [img]="Trash2" class="w-3.5 h-3.5"></lucide-icon>
                      </button>
                    </div>
                  </div>
                }
              </div>
            }
          </div>
        </div>
      } @empty {
        <div
          class="col-span-3 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-10 text-center"
        >
          <p class="text-gray-500 dark:text-gray-400">{{ 'ADMIN.PLANS.EMPTY' | translate }}</p>
        </div>
      }
    </div>
  }
</div>

<!-- Plan Create/Edit Dialog -->
@if (showPlanDialog()) {
  <div class="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black/50">
    <div
      class="bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-lg max-h-[92vh] flex flex-col"
    >
      <!-- Header -->
      <div
        class="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0"
      >
        <h2 class="text-base font-semibold text-gray-900 dark:text-white">
          {{
            editingPlan()
              ? ('ADMIN.PLANS.FORM.TITLE_EDIT' | translate)
              : ('ADMIN.PLANS.FORM.TITLE_CREATE' | translate)
          }}
        </h2>
        <button
          (click)="showPlanDialog.set(false)"
          class="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
        >
          <lucide-icon [img]="X" class="w-5 h-5 text-gray-500"></lucide-icon>
        </button>
      </div>
      <form
        [formGroup]="planForm"
        (ngSubmit)="submitPlan()"
        class="flex flex-col flex-1 overflow-hidden"
      >
        <!-- Scrollable body -->
        <div class="overflow-y-auto flex-1 px-5 py-4 space-y-3">
          @if (errorMsg()) {
            <div
              class="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400"
            >
              {{ errorMsg() }}
            </div>
          }
          <!-- Nom (ko'p til) -->
          <div>
            <label
              for=""
              class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5"
            >
              {{ 'ADMIN.PLANS.FORM.NAME' | translate }}
              <span class="text-red-500 normal-case">*</span>
            </label>
            <div class="grid grid-cols-2 gap-1.5">
              <input
                formControlName="name"
                type="text"
                placeholder="UZ: Standart"
                class="col-span-2 input-field"
              />
              <input
                formControlName="nameRu"
                type="text"
                placeholder="RU: Стандарт"
                class="input-field"
              />
              <input
                formControlName="nameEn"
                type="text"
                placeholder="EN: Standard"
                class="input-field"
              />
              <input
                formControlName="nameCyrl"
                type="text"
                placeholder="Кирилл: Стандарт"
                class="col-span-2 input-field"
              />
            </div>
          </div>

          <!-- Tavsif (ko'p til) -->
          <div>
            <label
              for=""
              class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5"
            >
              {{ 'ADMIN.PLANS.FORM.DESCRIPTION' | translate }}
            </label>
            <div class="grid grid-cols-2 gap-1.5">
              <input
                formControlName="description"
                type="text"
                placeholder="UZ: Tavsif..."
                class="col-span-2 input-field"
              />
              <input
                formControlName="descriptionRu"
                type="text"
                placeholder="RU: Описание..."
                class="input-field"
              />
              <input
                formControlName="descriptionEn"
                type="text"
                placeholder="EN: Description..."
                class="input-field"
              />
              <input
                formControlName="descriptionCyrl"
                type="text"
                placeholder="Кирилл: Тавсиф..."
                class="col-span-2 input-field"
              />
            </div>
          </div>

          <!-- Cheklovlar -->
          <div>
            <label
              for=""
              class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5"
            >
              Cheklovlar
            </label>
            <div class="grid grid-cols-3 gap-1.5">
              <div>
                <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">
                  {{ 'ADMIN.PLANS.FORM.MAX_STORES' | translate }}
                </p>
                <input formControlName="maxStores" type="number" min="1" class="input-field" />
              </div>
              <div>
                <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">
                  {{ 'ADMIN.PLANS.FORM.MAX_EMPLOYEES' | translate }}
                </p>
                <input formControlName="maxEmployees" type="number" min="1" class="input-field" />
              </div>
              <div>
                <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">
                  {{ 'ADMIN.PLANS.FORM.MAX_PRODUCTS' | translate }}
                </p>
                <input formControlName="maxProducts" type="number" min="0" class="input-field" />
              </div>
            </div>
            <p class="text-xs text-gray-400 mt-1">
              {{ 'ADMIN.PLANS.FORM.MAX_PRODUCTS_HINT' | translate }}
            </p>
          </div>

          <!-- Narx va tartib -->
          <div class="grid grid-cols-2 gap-1.5">
            <div>
              <label
                for=""
                class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5"
              >
                {{ 'ADMIN.PLANS.FORM.PRICE' | translate }}
              </label>
              <input formControlName="monthlyPrice" type="number" min="0" class="input-field" />
            </div>
            <div>
              <label
                for=""
                class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5"
              >
                {{ 'ADMIN.PLANS.FORM.ORDER' | translate }}
              </label>
              <input formControlName="displayOrder" type="number" min="0" class="input-field" />
            </div>
          </div>

          <!-- Ko‘rinish/ochiqlik -->
          <div class="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2">
            <label class="flex items-center justify-between gap-3 cursor-pointer">
              <div>
                <p class="text-sm font-medium text-gray-900 dark:text-white">Public ko‘rinishi</p>
                <p class="text-xs text-gray-500 dark:text-gray-400">
                  Bu tarif mijozlar va ochiq sahifada ko‘rinadimi?
                </p>
              </div>
              <input
                formControlName="isView"
                type="checkbox"
                class="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
            </label>
          </div>

          <!-- Modullar -->
          <div class="space-y-1.5">
            <label
              for=""
              class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide"
            >
              Modullar
            </label>
            <label
              for=""
              class="flex items-center gap-3 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              <input
                formControlName="moduleAttendance"
                type="checkbox"
                class="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
              />
              <span>
                <span class="block text-sm font-medium text-gray-900 dark:text-white"
                  >Attendance</span
                >
                <span class="block text-xs text-gray-500 dark:text-gray-400"
                  >Davomatni kuzatish moduli</span
                >
              </span>
            </label>
            <label
              for=""
              class="flex items-center gap-3 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              <input
                formControlName="moduleProductImages"
                type="checkbox"
                class="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <span>
                <span class="block text-sm font-medium text-gray-900 dark:text-white">{{
                  'ADMIN.PLANS.MODULE_PRODUCT_IMAGES' | translate
                }}</span>
                <span class="block text-xs text-gray-500 dark:text-gray-400">{{
                  'ADMIN.PLANS.MODULE_PRODUCT_IMAGES_HINT' | translate
                }}</span>
              </span>
            </label>
          </div>
        </div>

        <!-- Sticky footer -->
        <div class="flex gap-3 px-5 py-4 border-t border-gray-200 dark:border-gray-700 shrink-0">
          <button
            type="button"
            (click)="showPlanDialog.set(false)"
            class="flex-1 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            {{ 'ADMIN.PLANS.FORM.CANCEL' | translate }}
          </button>
          <button
            type="submit"
            [disabled]="planForm.invalid || submitting()"
            class="flex-1 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors font-medium"
          >
            {{
              submitting()
                ? ('ADMIN.PLANS.FORM.SAVING' | translate)
                : editingPlan()
                  ? ('ADMIN.PLANS.FORM.SAVE' | translate)
                  : ('ADMIN.PLANS.FORM.CREATE' | translate)
            }}
          </button>
        </div>
      </form>
    </div>
  </div>
}

<!-- Discount Create/Edit Dialog -->
@if (showDiscountDialog()) {
  <div class="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black/50">
    <div
      class="bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-sm"
    >
      <div
        class="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700"
      >
        <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
          {{
            editingDiscount()
              ? ('ADMIN.PLANS.DISCOUNT_FORM.TITLE_EDIT' | translate)
              : ('ADMIN.PLANS.DISCOUNT_FORM.TITLE_CREATE' | translate)
          }}
        </h2>
        <button
          (click)="showDiscountDialog.set(false)"
          class="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
        >
          <lucide-icon [img]="X" class="w-5 h-5 text-gray-500"></lucide-icon>
        </button>
      </div>
      <form [formGroup]="discountForm" (ngSubmit)="submitDiscount()" class="p-5 space-y-4">
        @if (errorMsg()) {
          <div
            class="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400"
          >
            {{ errorMsg() }}
          </div>
        }
        <div>
          <label for="" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{{
            'ADMIN.PLANS.DISCOUNT_FORM.DESCRIPTION' | translate
          }}</label>
          <input
            formControlName="description"
            type="text"
            [placeholder]="'ADMIN.PLANS.DISCOUNT_FORM.DESC_HINT' | translate"
            class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </div>
        <div>
          <label for="" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {{ 'ADMIN.PLANS.DISCOUNT_FORM.MIN_MONTHS' | translate }}
          </label>
          <input
            formControlName="minMonths"
            type="number"
            min="0"
            class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
          />
          <p class="text-xs text-gray-400 mt-1">
            {{ 'ADMIN.PLANS.DISCOUNT_FORM.MIN_MONTHS_HINT' | translate }}
          </p>
        </div>
        <div>
          <label for="" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{{
            'ADMIN.PLANS.DISCOUNT_FORM.PERCENT' | translate
          }}</label>
          <input
            formControlName="discountPercent"
            type="number"
            min="1"
            max="99"
            class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </div>
        @if (editingDiscount()) {
          <div class="flex items-center gap-3">
            <label for="" class="text-sm font-medium text-gray-700 dark:text-gray-300">{{
              'ADMIN.PLANS.DISCOUNT_FORM.IS_ACTIVE' | translate
            }}</label>
            <input formControlName="isActive" type="checkbox" class="w-4 h-4 rounded" />
          </div>
        }
        <div class="flex justify-end gap-3 pt-2">
          <button
            type="button"
            (click)="showDiscountDialog.set(false)"
            class="px-4 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            {{ 'ADMIN.PLANS.DISCOUNT_FORM.CANCEL' | translate }}
          </button>
          <button
            type="submit"
            [disabled]="discountForm.invalid || submitting()"
            class="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors font-medium"
          >
            {{
              submitting()
                ? ('ADMIN.PLANS.DISCOUNT_FORM.SAVING' | translate)
                : ('ADMIN.PLANS.DISCOUNT_FORM.SAVE' | translate)
            }}
          </button>
        </div>
      </form>
    </div>
  </div>
}
